import { useState, useEffect } from 'react';
import {
    Plus,
    Trash2,
    Key,
    Shield,
    User as UserIcon,
    X,
    Check,
} from 'lucide-react';
import { adminApi } from '../../lib/api';
import type { User } from '../../lib/types';
import { useAuthStore } from '../../stores/authStore';
import { useI18n } from '../../i18n';

export default function AdminUsers() {
    const { user: currentUser } = useAuthStore();
    const { t, formatRelativeTime } = useI18n();
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState<User | null>(null);

    // Form states
    const [newUser, setNewUser] = useState<{ username: string; password: string; role: 'admin' | 'user' }>({ username: '', password: '', role: 'user' });
    const [newPassword, setNewPassword] = useState('');

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            const response = await adminApi.getUsers();
            if (response.success) {
                setUsers(response.users);
            }
        } catch (error) {
            console.error('Failed to load users:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Create user
    const handleCreateUser = async () => {
        if (!newUser.username.trim() || !newUser.password.trim()) return;

        try {
            const response = await adminApi.createUser(newUser);
            if (response.success && response.user) {
                setUsers((prev) => [...prev, response.user]);
                setNewUser({ username: '', password: '', role: 'user' });
                setShowCreateModal(false);
            }
        } catch (error) {
            console.error('Failed to create user:', error);
            alert(t.admin.users.createFailed);
        }
    };

    // Delete user
    const handleDeleteUser = async (user: User) => {
        if (user.id === currentUser?.id) {
            alert(t.admin.users.cannotDeleteSelf);
            return;
        }
        if (!confirm(`"${user.username}" ${t.admin.users.deleteConfirm}`)) return;

        try {
            await adminApi.deleteUser(user.id);
            setUsers((prev) => prev.filter((u) => u.id !== user.id));
        } catch (error) {
            console.error('Failed to delete user:', error);
        }
    };

    // Reset password
    const handleResetPassword = async () => {
        if (!showPasswordModal || !newPassword.trim()) return;
        if (newPassword.length < 8) {
            alert(t.admin.users.passwordMinLength);
            return;
        }

        try {
            await adminApi.resetPassword(showPasswordModal.id, newPassword);
            setNewPassword('');
            setShowPasswordModal(null);
            alert(t.admin.users.passwordChanged);
        } catch (error) {
            console.error('Failed to reset password:', error);
        }
    };

    // Toggle role
    const handleToggleRole = async (user: User) => {
        if (user.id === currentUser?.id) {
            alert(t.admin.users.cannotChangeOwnRole);
            return;
        }

        const newRole = user.role === 'admin' ? 'user' : 'admin';
        if (!confirm(`"${user.username}" -> ${newRole}?`)) return;

        try {
            const response = await adminApi.updateRole(user.id, newRole);
            if (response.success && response.user) {
                setUsers((prev) =>
                    prev.map((u) => (u.id === user.id ? response.user : u))
                );
            }
        } catch (error) {
            console.error('Failed to update role:', error);
        }
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-dark-100">{t.admin.users.title}</h1>
                    <p className="text-dark-400 mt-1">{t.admin.users.description}</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="btn-primary"
                >
                    <Plus className="w-4 h-4" />
                    {t.admin.users.addUser}
                </button>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="spinner text-accent-500 w-8 h-8" />
                </div>
            ) : (
                <div className="card overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-dark-800">
                            <tr>
                                <th className="px-4 py-3 text-left text-sm font-medium text-dark-300">
                                    {t.admin.users.user}
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-dark-300">
                                    {t.admin.users.role}
                                </th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-dark-300">
                                    {t.admin.users.createdAt}
                                </th>
                                <th className="px-4 py-3 text-right text-sm font-medium text-dark-300">
                                    {t.admin.users.actions}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-dark-800">
                            {users.map((user) => (
                                <tr key={user.id} className="hover:bg-dark-800/50">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-dark-700 flex items-center justify-center">
                                                <UserIcon className="w-4 h-4 text-dark-400" />
                                            </div>
                                            <span className="font-medium text-dark-100">
                                                {user.username}
                                            </span>
                                            {user.id === currentUser?.id && (
                                                <span className="badge-primary">You</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => handleToggleRole(user)}
                                            disabled={user.id === currentUser?.id}
                                            className={`badge ${user.role === 'admin' ? 'badge-warning' : 'badge-primary'
                                                } cursor-pointer disabled:cursor-not-allowed`}
                                        >
                                            {user.role === 'admin' ? (
                                                <Shield className="w-3 h-3 mr-1" />
                                            ) : null}
                                            {user.role === 'admin' ? t.admin.users.admin : t.admin.users.user}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-dark-400">
                                        {formatRelativeTime(user.created_at)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => setShowPasswordModal(user)}
                                                className="btn-icon"
                                                title={t.admin.users.resetPassword}
                                            >
                                                <Key className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteUser(user)}
                                                disabled={user.id === currentUser?.id}
                                                className="btn-icon text-red-400 disabled:opacity-50"
                                                title={t.common.delete}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {users.length === 0 && (
                        <div className="text-center py-12 text-dark-500">
                            {t.admin.users.noUsers}
                        </div>
                    )}
                </div>
            )}

            {/* Create User Modal */}
            {showCreateModal && (
                <div className="modal-backdrop">
                    <div className="modal-content p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-dark-100">
                                {t.admin.users.addUser}
                            </h3>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="btn-icon"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-dark-300 mb-2">
                                    {t.admin.users.username}
                                </label>
                                <input
                                    type="text"
                                    value={newUser.username}
                                    onChange={(e) =>
                                        setNewUser((prev) => ({ ...prev, username: e.target.value }))
                                    }
                                    className="input"
                                    placeholder="username"
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-dark-300 mb-2">
                                    {t.admin.users.password}
                                </label>
                                <input
                                    type="password"
                                    value={newUser.password}
                                    onChange={(e) =>
                                        setNewUser((prev) => ({ ...prev, password: e.target.value }))
                                    }
                                    className="input"
                                    placeholder="8+ characters"
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-dark-300 mb-2">{t.admin.users.role}</label>
                                <select
                                    value={newUser.role}
                                    onChange={(e) =>
                                        setNewUser((prev) => ({
                                            ...prev,
                                            role: e.target.value as 'admin' | 'user',
                                        }))
                                    }
                                    className="input"
                                >
                                    <option value="user">{t.admin.users.user}</option>
                                    <option value="admin">{t.admin.users.admin}</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="btn-secondary"
                            >
                                {t.common.cancel}
                            </button>
                            <button onClick={handleCreateUser} className="btn-primary">
                                <Check className="w-4 h-4" />
                                {t.common.add}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reset Password Modal */}
            {showPasswordModal && (
                <div className="modal-backdrop">
                    <div className="modal-content p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-dark-100">
                                {t.admin.users.resetPassword}
                            </h3>
                            <button
                                onClick={() => setShowPasswordModal(null)}
                                className="btn-icon"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <p className="text-dark-400 mb-4">
                            <span className="font-medium text-dark-200">
                                {showPasswordModal.username}
                            </span>
                            {t.admin.users.enterNewPassword}
                        </p>

                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="input mb-4"
                            placeholder={t.admin.users.newPassword}
                        />

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowPasswordModal(null)}
                                className="btn-secondary"
                            >
                                {t.common.cancel}
                            </button>
                            <button onClick={handleResetPassword} className="btn-primary">
                                <Check className="w-4 h-4" />
                                {t.common.save}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
